/**
 * OpeningBalanceScreen — Year Initialization
 * Editable Dr & Cr columns for M_GeneralLedger opening balances
 * Excel paste support, CSV import, Dr=Cr validation
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useGlobal } from '../../store/GlobalStoreProvider';
import { GK } from '../../store/GlobalKeys';
import { Save, Upload, Download, AlertTriangle } from 'lucide-react';
import FinanceReportGrid from '../../components/erp/FinanceReportGrid';
import { erpToast } from '../../components/erp/ERPToast';
import '../../styles/erp-theme.css';
import { API_BASE as API, getToken} from '../../lib/api';

const tkn = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' });
const autoSelect = (e) => e.target.select();

const OpeningBalanceScreen = ({ theme = 'light' }) => {
  const companyName = useGlobal(GK.COMPANY_NAME, 'Company');
  const fiscalYear = useGlobal(GK.FISCAL_YEAR, '2025-2026');
  const [accounts, setAccounts] = useState([]);
  const [modified, setModified] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [withZeros, setWithZeros] = useState(false);
  const [filterText, setFilterText] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/financial-statements/opening-balances?branchId=${branchId}&withZeros=${withZeros}`, { headers: hdrs() });
      const j = await r.json();
      if (j.success) setAccounts(j.data.accounts || []);
    } catch { erpToast.error('Failed to load'); }
    setLoading(false);
  }, [branchId, withZeros]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { (async () => { try { const r = await fetch(`${API}/api/finance-reports/branches`, { headers: hdrs() }); const j = await r.json(); if (j.success) setBranches(j.data || []); } catch {} })(); }, []);

  const handleEdit = (accountId, field, value) => {
    const key = accountId;
    const current = modified.get(key) || accounts.find(a => a.AccountId === accountId) || {};
    setModified(prev => new Map(prev).set(key, { ...current, AccountId: accountId, [field]: parseFloat(value) || 0 }));
  };

  // Merge modified into display
  const displayData = useMemo(() => {
    const filtered = filterText ? accounts.filter(a => (a.AccountName || '').toLowerCase().includes(filterText.toLowerCase()) || (a.AccountId || '').toLowerCase().includes(filterText.toLowerCase())) : accounts;
    return filtered.map(a => {
      const mod = modified.get(a.AccountId);
      return mod ? { ...a, DrOpenBal: mod.DrOpenBal ?? a.DrOpenBal, CrOpenBal: mod.CrOpenBal ?? a.CrOpenBal } : a;
    });
  }, [accounts, modified, filterText]);

  const totalDr = useMemo(() => displayData.reduce((s, a) => s + (parseFloat(a.DrOpenBal) || 0), 0), [displayData]);
  const totalCr = useMemo(() => displayData.reduce((s, a) => s + (parseFloat(a.CrOpenBal) || 0), 0), [displayData]);
  const isBalanced = Math.abs(totalDr - totalCr) < 1;

  const handleSave = async () => {
    if (!isBalanced) { erpToast.error(`Cannot save: Dr (${totalDr.toFixed(2)}) != Cr (${totalCr.toFixed(2)}). Diff: ${Math.abs(totalDr - totalCr).toFixed(2)}`); return; }
    if (modified.size === 0) { erpToast.warning('No changes to save'); return; }
    setSaving(true);
    try {
      const entries = Array.from(modified.values());
      const r = await fetch(`${API}/api/financial-statements/opening-balances/save`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ entries }) });
      const j = await r.json();
      if (j.success) { erpToast.success(`${j.updated} opening balances saved`); setModified(new Map()); loadData(); }
      else erpToast.error(j.error);
    } catch { erpToast.error('Save failed'); }
    setSaving(false);
  };

  // CSV Import
  const handleCSVImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const text = ev.target?.result;
        const lines = text.split('\n').filter(l => l.trim());
        const rows = lines.slice(1).map(line => {
          const [AccountId, , DrOpenBal, CrOpenBal] = line.split(',').map(s => s.trim().replace(/"/g, ''));
          return { AccountId, DrOpenBal: parseFloat(DrOpenBal) || 0, CrOpenBal: parseFloat(CrOpenBal) || 0 };
        }).filter(r => r.AccountId);
        const r = await fetch(`${API}/api/financial-statements/opening-balances/import-csv`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ rows }) });
        const j = await r.json();
        if (j.success) {
          const newMod = new Map(modified);
          j.data.validRows.forEach(r => newMod.set(r.AccountId, r));
          setModified(newMod);
          erpToast.success(`${j.data.validRows.length} rows imported. ${j.data.errors.length} errors.`);
          if (j.data.errors.length > 0) j.data.errors.slice(0, 3).forEach(e => erpToast.warning(e));
        }
      } catch { erpToast.error('CSV parse failed'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // Excel paste handler
  const handlePaste = (e) => {
    const text = e.clipboardData?.getData('text');
    if (!text || !text.includes('\t')) return;
    e.preventDefault();
    const lines = text.split('\n').filter(l => l.trim());
    const newMod = new Map(modified);
    let count = 0;
    for (const line of lines) {
      const [accId, , dr, cr] = line.split('\t');
      if (accId && accounts.find(a => a.AccountId === accId.trim())) {
        newMod.set(accId.trim(), { AccountId: accId.trim(), DrOpenBal: parseFloat(dr) || 0, CrOpenBal: parseFloat(cr) || 0 });
        count++;
      }
    }
    if (count > 0) { setModified(newMod); erpToast.success(`Pasted ${count} rows`); }
  };

  return (
    <div className="erp-module" data-testid="opening-balance-screen" onPaste={handlePaste}>
      <div className="erp-screen-header">
        <span className="erp-screen-title">Opening Balance Adjustment</span>
        <span style={{ fontSize: 12, fontWeight: 600, marginLeft: 16, color: isBalanced ? 'var(--erp-success)' : 'var(--erp-danger)' }}>
          Dr: {totalDr.toLocaleString('en-IN', { minimumFractionDigits: 2 })} | Cr: {totalCr.toLocaleString('en-IN', { minimumFractionDigits: 2 })} | {isBalanced ? 'BALANCED' : `DIFF: ${Math.abs(totalDr - totalCr).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
        </span>
        <div className="erp-screen-actions">
          <input type="text" value={filterText} onChange={e => setFilterText(e.target.value)} placeholder="Search account..." className="erp-input" style={{ width: 160, height: 26, fontSize: 11 }} />
          <label className="erp-btn erp-btn-ghost" style={{ cursor: 'pointer' }}><Upload size={13} /> Import CSV<input type="file" accept=".csv" onChange={handleCSVImport} style={{ display: 'none' }} /></label>
          <button className="erp-btn erp-btn-primary" onClick={handleSave} disabled={saving || !isBalanced} style={!isBalanced ? { opacity: 0.5 } : undefined} data-testid="ob-save-btn"><Save size={13} /> Save ({modified.size})</button>
        </div>
      </div>

      {!isBalanced && modified.size > 0 && (
        <div style={{ padding: '6px var(--erp-sp-3)', background: 'var(--erp-danger-light)', borderBottom: '1px solid var(--erp-danger)', fontSize: 11, color: 'var(--erp-danger)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <AlertTriangle size={14} /> Save blocked: Debit ({totalDr.toFixed(2)}) does not equal Credit ({totalCr.toFixed(2)}). Difference: {Math.abs(totalDr - totalCr).toFixed(2)}
        </div>
      )}

      {/* Filters */}
      <div style={{ padding: 'var(--erp-sp-3)', borderBottom: '1px solid var(--erp-border)', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="erp-form-row" style={{ margin: 0 }}><label className="erp-form-label">Company</label><div className="erp-form-control"><input value={companyName} readOnly className="erp-input" style={{ background: 'var(--erp-hover)', fontWeight: 600, width: 200 }} /></div></div>
        <div className="erp-form-row" style={{ margin: 0 }}><label className="erp-form-label">Fiscal Year</label><div className="erp-form-control"><input value={fiscalYear} readOnly className="erp-input" style={{ width: 100, background: 'var(--erp-hover)' }} /></div></div>
        <div className="erp-form-row" style={{ margin: 0 }}><label className="erp-form-label">Branch</label><div className="erp-form-control"><select value={branchId} onChange={e => setBranchId(e.target.value)} className="erp-input erp-select" style={{ width: 140 }}><option value="">Main Branch</option>{branches.map(b => <option key={b.ID} value={b.ID}>{b.Name}</option>)}</select></div></div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="radio" name="obzeros" checked={withZeros} onChange={() => setWithZeros(true)} /> With Zeros</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="radio" name="obzeros" checked={!withZeros} onChange={() => setWithZeros(false)} /> Without Zeros</label>
        <button className="erp-btn erp-btn-ghost" onClick={loadData}><Download size={13} /> Refresh</button>
      </div>

      {/* Editable Grid */}
      <div className="erp-grid-wrapper" style={{ flex: 1, margin: 0, borderRadius: 0 }}>
        <div className="erp-grid-scroll">
          <table className="erp-grid" data-testid="ob-grid">
            <thead><tr>
              <th style={{ width: 110 }}>Account Id</th>
              <th style={{ minWidth: 200 }}>Account Name</th>
              <th style={{ width: 150, textAlign: 'right' }}>Debit</th>
              <th style={{ width: 150, textAlign: 'right' }}>Credit</th>
              <th style={{ width: 150, textAlign: 'right' }}>Closing Balance</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={5} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>Loading...</td></tr>
              : displayData.length === 0 ? <tr><td colSpan={5} style={{ padding: 48, textAlign: 'center', color: 'var(--erp-text-muted)' }}>No accounts found</td></tr>
              : displayData.slice(0, 100).map((a, i) => {
                const isMod = modified.has(a.AccountId);
                const closing = (a.DrOpenBal || 0) - (a.CrOpenBal || 0);
                return (
                  <tr key={a.AccountId} style={{ background: isMod ? 'var(--erp-warning-light)' : (i % 2 === 0 ? 'var(--erp-surface)' : 'var(--erp-surface-alt)') }} data-testid={`ob-row-${a.AccountId}`}>
                    <td style={{ fontFamily: 'var(--erp-font-mono)', fontSize: 11 }}>{a.AccountId}</td>
                    <td style={{ fontSize: 12 }}>{a.AccountName}</td>
                    <td style={{ padding: 2 }}><input type="number" value={a.DrOpenBal || ''} onFocus={autoSelect} onChange={e => handleEdit(a.AccountId, 'DrOpenBal', e.target.value)} className="erp-input" style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, width: '100%', border: isMod ? '2px solid var(--erp-warning)' : undefined }} /></td>
                    <td style={{ padding: 2 }}><input type="number" value={a.CrOpenBal || ''} onFocus={autoSelect} onChange={e => handleEdit(a.AccountId, 'CrOpenBal', e.target.value)} className="erp-input" style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, width: '100%', border: isMod ? '2px solid var(--erp-warning)' : undefined }} /></td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: 12, color: closing >= 0 ? 'var(--erp-text)' : 'var(--erp-danger)' }}>{Math.abs(closing).toLocaleString('en-IN', { minimumFractionDigits: 2 })} {closing >= 0 ? 'Dr' : 'Cr'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="erp-pagination"><span>{displayData.length} accounts (showing first 100)</span></div>
      </div>
    </div>
  );
};

export default OpeningBalanceScreen;
